import { useEffect, useState } from 'react';
import labAnalyteService from '../api/services/labAnalyteService';

/**
 * Debounced autocomplete hook for the form-builder Expected Lab Results input.
 * Returns the most recent suggestion list for the given query.
 *
 * @param {string} query
 * @param {{ limit?: number, debounceMs?: number, enabled?: boolean }} [options]
 * @returns {{ suggestions: Array, loading: boolean, error: Error|null }}
 */
export const useLabAnalyteSuggestions = (query, options = {}) => {
  const { limit = 20, debounceMs = 250, enabled = true } = options;
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled) {
      setSuggestions([]);
      return undefined;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        setError(null);
        const results = await labAnalyteService.getSuggestions(query ?? '', limit, controller.signal);
        if (!controller.signal.aborted) {
          setSuggestions(Array.isArray(results) ? results : []);
        }
      } catch (err) {
        if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
        setError(err);
        setSuggestions([]);
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }, debounceMs);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query, limit, debounceMs, enabled]);

  return { suggestions, loading, error };
};

export default useLabAnalyteSuggestions;
